import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { interval, map, Subscription, takeWhile } from 'rxjs';

import { COMMON_STANDALONE_IMPORTS } from '@shared/common-imports';
import { FormErrorComponent } from '@shared/components';
import { RequiredLabelDirective } from '@shared/directives';
import { ToastsService, AuthService } from '@shared/services';
import { FormGenerator, FormGroupExtension } from '@shared/utils';

import { AuthCardComponent } from '../auth-card/auth-card.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  imports: [
    ...COMMON_STANDALONE_IMPORTS,
    TranslateModule,
    FormErrorComponent,
    PasswordModule,
    ButtonModule,
    RequiredLabelDirective,
    AuthCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit, OnDestroy {
  loginComponentLoader = 'loginComponentLoader';

  editForm = FormGenerator.initLoginForm();
  twoFactorEnabled = signal(false);
  secondsLeft = signal(0);
  sub: Subscription;

  constructor(
    private translate: TranslateService,
    private toastsService: ToastsService,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const email = this.activatedRoute.snapshot.queryParamMap.get('email');
    this.editForm = FormGenerator.initLoginForm(email);
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  onSubmit(generateNewTwoFactorCode = false): void {
    if (FormGroupExtension.hasAnyValidationError(this.editForm) && !generateNewTwoFactorCode) {
      FormGroupExtension.markFormAsDirtyAndTouched(this.editForm);
      this.toastsService.showError(
        FormGroupExtension.getFormValidationErrorsMessage(this.editForm, this.translate),
        this.translate.instant('error.invalidForm')
      );
      return;
    }

    this.authService
      .login(
        this.loginComponentLoader,
        this.editForm.controls.email.getRawValue(),
        this.editForm.controls.password.getRawValue(),
        !generateNewTwoFactorCode ? this.editForm.controls.twoFactorCode?.getRawValue() : null
      )
      .subscribe({
        next: (response) => {
          if (response.user) {
            this.toastsService.showSuccess(
              this.translate.instant('auth.login.welcomeBack') + ` ${response.user.fullName ?? response.user.email}!`
            );
          } else if (!response.user && response.twoFactorType) {
            this.toastsService.showInfo(this.translate.instant('auth.login.twoFactorRequiredToast'));
            this.twoFactorEnabled.set(true);
            this.editForm.controls.email.disable();
            this.editForm.controls.password.disable();
            this.editForm.controls.twoFactorCode.setValidators([Validators.required, Validators.minLength(4)]);
            this.startTimer(60);
          }
        },
        error: (error) => {
          this.toastsService.showError(error);
        },
      });
  }

  startTimer(seconds: number = 30) {
    this.secondsLeft.set(+seconds);
    this.sub = interval(1000)
      .pipe(
        map((i) => seconds - i - 1),
        takeWhile((time) => time >= 0)
      )
      .subscribe({
        next: (timeLeft) => this.secondsLeft.set(timeLeft),
        complete: () => this.secondsLeft.set(0),
      });
  }
}
